import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarIcon, Clock, CheckCircle } from 'lucide-react';
import { format, addDays } from 'date-fns';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Appointments = () => {
  const [services, setServices] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    patient_name: '',
    phone: '',
    email: '',
    service_id: '',
    preferred_time: '',
    notes: ''
  });

  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots();
    }
  }, [selectedDate]);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services?visible_only=true`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
      toast.error('Failed to load services');
    }
  };

  const fetchAvailableSlots = async () => {
    if (!selectedDate) return;
    
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await axios.get(`${API}/appointments/available-slots?date=${dateStr}`);
      setAvailableSlots(response.data.slots || []);
      
      if (response.data.message) {
        toast.info(response.data.message);
      }
    } catch (error) {
      console.error('Error fetching slots:', error);
      toast.error('Failed to load available slots');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate || !formData.preferred_time) {
      toast.error('Please select date and time');
      return;
    }

    setLoading(true);
    
    try {
      const appointmentData = {
        ...formData,
        preferred_date: format(selectedDate, 'yyyy-MM-dd')
      };
      
      await axios.post(`${API}/appointments`, appointmentData);
      
      setSubmitted(true);
      toast.success('Appointment booked successfully! Check your email for confirmation.');
    } catch (error) {
      console.error('Error booking appointment:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to book appointment';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const disableSundays = (date) => {
    return date.getDay() === 0 || date < new Date();
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-cyan-50 py-12 px-4" data-testid="success-message">
        <Card className="max-w-2xl w-full shadow-2xl">
          <CardContent className="p-12 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Appointment Confirmed!
            </h2>
            <p className="text-lg text-gray-700 mb-2">
              Your appointment has been successfully booked.
            </p>
            <p className="text-gray-600 mb-8">
              You will receive a confirmation email and WhatsApp message shortly.
            </p>
            <div className="bg-cyan-50 p-6 rounded-xl mb-8">
              <div className="text-sm text-gray-600 mb-2">Appointment Details</div>
              <div className="text-xl font-semibold text-gray-900">
                {format(selectedDate, 'MMMM dd, yyyy')} at {formData.preferred_time}
              </div>
            </div>
            <div className="space-y-3">
              <Button
                onClick={() => {
                  setSubmitted(false);
                  setFormData({
                    patient_name: '',
                    phone: '',
                    email: '',
                    service_id: '',
                    preferred_time: '',
                    notes: ''
                  });
                  setSelectedDate(null);
                }}
                className="w-full bg-cyan-600 hover:bg-cyan-700"
                data-testid="book-another-button"
              >
                Book Another Appointment
              </Button>
              <a href="https://wa.me/919876543210" target="_blank" rel="noopener noreferrer" className="block">
                <Button variant="outline" className="w-full" data-testid="whatsapp-confirmation-button">
                  Contact on WhatsApp
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <main className="py-12 bg-gradient-to-br from-blue-50 to-cyan-50 min-h-screen" data-testid="appointments-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
            Book an Appointment
          </h1>
          <p className="text-lg text-gray-700">
            Schedule your free consultation at Dr. B. Rajendra Kumar's Clinic
          </p>
        </div>

        {/* Booking Form */}
        <Card className="shadow-2xl">
          <CardHeader className="bg-gradient-to-r from-cyan-600 to-emerald-600 text-white rounded-t-lg">
            <CardTitle className="text-2xl">Appointment Details</CardTitle>
          </CardHeader>
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="patient_name">Full Name *</Label>
                  <Input
                    id="patient_name"
                    data-testid="patient-name-input"
                    value={formData.patient_name}
                    onChange={(e) => handleInputChange('patient_name', e.target.value)}
                    required
                    placeholder="Enter your full name"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    data-testid="phone-input"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    required
                    placeholder="+91 9876543210"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="email-input"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required
                  placeholder="your.email@example.com"
                />
              </div>

              {/* Service Selection */}
              <div>
                <Label htmlFor="service">Service *</Label>
                <Select
                  value={formData.service_id}
                  onValueChange={(value) => handleInputChange('service_id', value)}
                  required
                >
                  <SelectTrigger data-testid="service-select">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((service) => (
                      <SelectItem key={service.id} value={service.id}>
                        {service.name} ({service.duration} min)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Date and Time Selection */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label>Preferred Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                        data-testid="date-picker-button"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {selectedDate ? format(selectedDate, 'PPP') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={setSelectedDate}
                        disabled={disableSundays}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <p className="text-xs text-gray-500 mt-1">Clinic closed on Sundays</p>
                </div>

                <div>
                  <Label htmlFor="time">Preferred Time *</Label>
                  <Select
                    value={formData.preferred_time}
                    onValueChange={(value) => handleInputChange('preferred_time', value)}
                    disabled={!selectedDate}
                    required
                  >
                    <SelectTrigger data-testid="time-select">
                      <SelectValue placeholder="Select time slot" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableSlots.map((slot) => (
                        <SelectItem
                          key={slot.time}
                          value={slot.time}
                          disabled={!slot.available}
                        >
                          {slot.time} {!slot.available && '(Booked)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">Hours: 5:00 AM - 11:00 AM</p>
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label htmlFor="notes">Additional Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  data-testid="notes-textarea"
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Any specific health concerns or information you'd like to share"
                  rows={4}
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full bg-cyan-600 hover:bg-cyan-700 text-lg py-6"
                disabled={loading}
                data-testid="submit-appointment-button"
              >
                {loading ? (
                  <>
                    <div className="spinner w-5 h-5 mr-2"></div>
                    Booking...
                  </>
                ) : (
                  'Confirm Appointment'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Info Box */}
        <Card className="mt-8 bg-cyan-50 border-cyan-200">
          <CardContent className="p-6">
            <h3 className="font-semibold text-gray-900 mb-2">Important Information:</h3>
            <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
              <li>All services are completely free of charge</li>
              <li>Please arrive 10 minutes before your appointment time</li>
              <li>Bring any previous medical records if available</li>
              <li>Clinic is open Monday to Saturday, 5:00 AM - 11:00 AM</li>
              <li>You will receive confirmation via email and WhatsApp</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </main>
  );
};

export default Appointments;
